import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useAudioPlayer} from 'react-use-audio-player';
import useStreamingAudio from '@contentlab/hooks/use-streaming-audio';
import createAuthHeaders from '@contentlab/utils/authHeaders';
import ttsCache from '@contentlab/utils/ttsPreviewCache';
import {getEnv} from '@/config/environment';

// Cache helpers moved to util

export interface UseTtsPreviewOptions {
  volume?: number;
  onPlayStart?: () => void;
  onPlayEnd?: () => void;
  onBufferComplete?: () => void;
  abortOnStop?: boolean;
  bufferChunks?: number;
}

export default function useTtsPreview({
  volume = 1,
  onPlayStart,
  onPlayEnd,
  onBufferComplete,
  abortOnStop = true,
  bufferChunks = 1,
}: UseTtsPreviewOptions = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isBuffering, setIsBuffering] = useState(false);
  const currentTextRef = useRef<string>('');
  const currentVoiceIdRef = useRef<string>('');
  const isUsingStreamingRef = useRef<boolean>(false);
  const volumeRef = useRef<number>(volume);

  // Update volume ref when it changes
  volumeRef.current = volume;

  // Initialize streaming audio hook
  const streamingAudio = useStreamingAudio({
    onPlayStart: () => {
      setIsLoading(false);
      setError(null);
      onPlayStart?.();
    },
    onPlayEnd: () => {
      setAudioUrl(null);
      onPlayEnd?.();
    },
    onAudioReady: (url, mimeType) => {
      // Cache the audio for future use
      const cacheKey = ttsCache.makeTtsKey(
        currentTextRef.current,
        currentVoiceIdRef.current
      );
      ttsCache.set(cacheKey, {
        audioUrl: url,
        mimeType,
      });
      // Stop buffering when streaming is complete
      setIsBuffering(false);
      onBufferComplete?.();
    },
    abortOnStop,
    bufferChunks,
    volume: volumeRef.current,
  });

  const {
    load,
    stop: stopPlayback,
    play,
    pause,
    setVolume,
    isPlaying: audioPlayerIsPlaying,
    duration,
  } = useAudioPlayer();

  useEffect(() => {
    setVolume(volumeRef.current);
  }, [volume]);

  const cleanupObjectUrl = useCallback(() => {
    if (audioUrl && !ttsCache.isCached(audioUrl)) {
      URL.revokeObjectURL(audioUrl);
    }
  }, [audioUrl]);

  const stop = useCallback(() => {
    try {
      stopPlayback();
    } catch {
      // ignore player stop/unload failures
    }
    // Stop streaming audio if active
    streamingAudio.stop();
    // Ensure UI resets immediately
    setIsLoading(false);
    cleanupObjectUrl();
    // Clear current object URL so UI returns to "Play" state
    setAudioUrl(null);
    // Reset streaming flag when stopping
    isUsingStreamingRef.current = false;
    // Only stop buffering if we're aborting the request
    // If abortOnStop is false, keep buffering in background
    if (abortOnStop) {
      setIsBuffering(false);
    }
  }, [stopPlayback, cleanupObjectUrl, streamingAudio, abortOnStop]);

  const pauseAudio = useCallback(() => {
    try {
      pause();
    } catch {
      // ignore pause failures
    }
  }, [pause]);

  const playAudio = useCallback(() => {
    try {
      setVolume(volumeRef.current);
      play();
    } catch {
      // ignore play failures
    }
  }, [play, setVolume]);

  const preview = useCallback(
    async ({
      text,
      voice_id,
      project_id,
      brand_id,
    }: {
      text: string;
      voice_id: string;
      project_id: string;
      brand_id: string;
    }) => {
      // Toggle: if currently playing or loading, stop instead of restarting
      const currentlyPlaying = isUsingStreamingRef.current
        ? streamingAudio.isPlaying || streamingAudio.isLoading
        : audioPlayerIsPlaying;
      if (currentlyPlaying) {
        stop();
        return;
      }

      // Track current text and voice for caching
      currentTextRef.current = text;
      currentVoiceIdRef.current = voice_id;

      // Reset streaming state for new preview call
      isUsingStreamingRef.current = false;
      // Start buffering for new audio
      setIsBuffering(true);

      // Only clean up audio player, not streaming audio
      // streamingAudio.play() will call stop() internally to clean itself up
      try {
        stopPlayback();
      } catch {
        // ignore player stop/unload failures
      }
      cleanupObjectUrl();
      setError(null);
      setIsLoading(true);
      setAudioUrl(null);

      try {
        // Check cache first
        const cacheKey = ttsCache.makeTtsKey(text, voice_id);
        const cached = ttsCache.get(cacheKey);

        if (cached) {
          // Use cached audio with traditional player
          isUsingStreamingRef.current = false;
          setAudioUrl(cached.audioUrl);
          const mime = cached.mimeType;
          const guessedFormat = mime
            ? mime.split('/')[1]?.toLowerCase().replace('mpeg', 'mp3')
            : undefined;
          load(cached.audioUrl, {
            html5: true,
            initialVolume: volumeRef.current,
            initialMute: false,
            ...(guessedFormat ? {format: guessedFormat} : {}),
            autoplay: false, // We'll play it manually after loading
            onload: () => {
              setIsBuffering(false);
              onBufferComplete?.();
              // Ensure volume is set before playing
              setVolume(volumeRef.current);
              playAudio();
            },
            onplay: () => {
              // Audio actually started playing
              onPlayStart?.();
            },
            onend: () => {
              setAudioUrl(null);
              onPlayEnd?.();
            },
            onstop: () => {
              setAudioUrl(null);
              onPlayEnd?.();
            },
          });
          setIsLoading(false);
          return;
        }

        // Not cached - stream for immediate playback
        // The onAudioReady callback will cache the result
        isUsingStreamingRef.current = true;

        const url = `${getEnv().VITE_CONTENT_STUDIO_API_HOST}/api/v1/ai_avatars/tts/preview`;
        const headers = await createAuthHeaders({
          brandId: brand_id,
          accept: 'text/event-stream',
          contentType: 'application/json',
        });

        await streamingAudio.play(url, {
          method: 'POST',
          headers,
          body: JSON.stringify({text, voice_id, project_id}),
        });
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : 'Failed to preview audio'
        );
      } finally {
        setIsLoading(false);
      }
    },
    [
      stop,
      cleanupObjectUrl,
      load,
      audioPlayerIsPlaying,
      streamingAudio,
      playAudio,
    ]
  );

  return useMemo(
    () => ({
      isLoading: isLoading || streamingAudio.isLoading,
      error: error || streamingAudio.error,
      audioUrl,
      isBuffering,
      preview,
      stop,
      pause: pauseAudio,
      play: playAudio,
      duration: streamingAudio.duration || duration,
      isPlaying: isUsingStreamingRef.current
        ? streamingAudio.isPlaying
        : audioPlayerIsPlaying,
    }),
    [
      isLoading,
      streamingAudio.isLoading,
      error,
      streamingAudio.error,
      audioUrl,
      isBuffering,
      preview,
      stop,
      pauseAudio,
      playAudio,
      duration,
      streamingAudio.duration,
      streamingAudio.isPlaying,
      audioPlayerIsPlaying,
    ]
  );
}
